import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Wallet, AlertCircle, Users, Receipt, FileSpreadsheet, Loader2 } from "lucide-react";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const PAY_STYLES = {
  Unpaid: "bg-red-100 text-red-700",
  Partial: "bg-amber-100 text-amber-800",
};

export default function PendingPayments() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/reports/pending-payments").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/outstanding", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "outstanding_report.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Outstanding report downloaded");
    } catch {
      toast.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <div className="p-8 text-sm text-slate-400" data-testid="payments-loading">Loading…</div>;

  const cards = [
    { label: "Total Outstanding", value: inr(data.total_outstanding), icon: Wallet, cls: "text-red-600" },
    { label: "Pending Bills", value: data.pending_bill_count, icon: Receipt, cls: "text-amber-600" },
    { label: "Parties with Dues", value: data.party_count, icon: Users, cls: "text-slate-700" },
  ];

  return (
    <div className="p-8" data-testid="pending-payments-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Pending Payments</h1>
          <p className="text-slate-500 text-sm">Unpaid &amp; partially paid bills, party-wise outstanding</p>
        </div>
        <button onClick={downloadExcel} disabled={downloading || !data.pending_bill_count} data-testid="download-outstanding-btn"
          className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
          Download Excel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {cards.map(({ label, value, icon: Icon, cls }) => (
          <div key={label} className="stat-card" data-testid={`payments-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{label}</span>
              <Icon size={22} className={cls} />
            </div>
            <div className={`text-3xl font-semibold mt-3 ${cls}`}>{value}</div>
          </div>
        ))}
      </div>

      {data.pending_bill_count === 0 ? (
        <div className="bg-white border border-slate-100 rounded-xl p-10 text-center text-slate-400 text-sm" data-testid="no-pending-payments">
          <AlertCircle size={32} className="mx-auto mb-2 text-green-500" />
          No pending payments — all bills are settled!
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-100 rounded-xl">
            <div className="p-4 border-b border-slate-100 text-sm font-semibold text-slate-700">Party-wise Outstanding</div>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Party</th>
                  <th className="text-right px-4 py-2">Bills</th>
                  <th className="text-right px-4 py-2">Billed</th>
                  <th className="text-right px-4 py-2">Received</th>
                  <th className="text-right px-4 py-2">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {data.parties.map((p) => (
                  <tr key={p.party_name} className="border-t border-slate-100" data-testid={`party-due-row-${p.party_name}`}>
                    <td className="px-4 py-2 font-medium">{p.party_name}</td>
                    <td className="px-4 py-2 text-right">{p.bills}</td>
                    <td className="px-4 py-2 text-right">{inr(p.billed)}</td>
                    <td className="px-4 py-2 text-right text-green-600">{inr(p.received)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-red-600">{inr(p.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

          <div className="bg-white border border-slate-100 rounded-xl">
            <div className="p-4 border-b border-slate-100 text-sm font-semibold text-slate-700">Pending Bills</div>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Bill No</th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Party</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {data.bills.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`pending-bill-row-${b.bill_no}`}>
                    <td className="px-4 py-2 font-mono text-[#0b5cc4]">
                      <Link to={`/portal/bills/${b.id}/print`} className="hover:underline">{b.bill_no}</Link>
                    </td>
                    <td className="px-4 py-2">{b.date}</td>
                    <td className="px-4 py-2">{b.party_name}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${PAY_STYLES[b.payment_status] || PAY_STYLES.Unpaid}`}>{b.payment_status}</span>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-red-600">{inr(b.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </div>
      )}
    </div>
  );
}
